import React, { useState, useEffect } from 'react';
import { HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { FaCog, FaInbox } from 'react-icons/fa';
import { Workstation } from '../../../types';
import { toast } from 'react-toastify';
import {
  updateWorkstationById,
  deleteWorkstationById,
} from '../../../../../../crud/management_api/workstations';
import CreateWorkstation from './components/CreateWorkstation/CreateWorkstation';
import EditWorkstation from './components/EditWorkstation/EditWorkstation';
import DeleteWorkstation from './components/DeleteWorkstation/DeleteWorkstation';
import { TerminalOption } from './components/utils/utils';
import './Table.scss';

interface TableProps {
  data: Workstation[];
  terminals: TerminalOption[];
  setWorkstations: React.Dispatch<React.SetStateAction<Workstation[]>>;
  setFilteredWorkstation: React.Dispatch<React.SetStateAction<Workstation[]>>;
  externalShowSelection: boolean;
  externalSelectedWorkstations: Workstation[];
  externalSetSelectedWorkstations: React.Dispatch<React.SetStateAction<Workstation[]>>;
  externalShowCreateModal: boolean;
  externalSetShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Table: React.FC<TableProps> = ({ 
  data, 
  terminals, 
  setWorkstations, 
  setFilteredWorkstation,
  externalShowSelection,
  externalSelectedWorkstations,
  externalSetSelectedWorkstations,
  externalShowCreateModal,
  externalSetShowCreateModal
}) => {
  // Use external state for selection
  const showSelection = externalShowSelection;
  const selectedWorkstations = externalSelectedWorkstations;
  const setSelectedWorkstations = externalSetSelectedWorkstations;
  const showCreateModal = externalShowCreateModal;
  const setShowCreateModal = externalSetShowCreateModal;
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentWorkstation, setCurrentWorkstation] = useState<Workstation | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isOnlyOneCaja, setOnlyOneCaja] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const cajas = data.filter((w) => w.isCaja);
    setOnlyOneCaja(cajas.length === 1);
  }, [data]);

  const handleShowDeleteModal = (workstation: Workstation) => {
    setCurrentWorkstation(workstation);
    setShowDeleteModal(true);
  };

  const handleShowEditModal = async (workstation: Workstation) => {
    if (workstation) {
      setCurrentWorkstation(workstation);
      setShowEditModal(true);
    } else {
      console.error('Failed to fetch workstation for editing.');
      toast.error('La estación de trabajo no puede ser editada');
    }
  };

  const toggleItemState = (id: string) => {
    const ws = data.find((w) => w.workstation_id === id);
    if (!ws) return;
    updateWorkstationById(Number(id), { state: !ws.state })
      .then(() => {
        toast.success('Estado de la estación de trabajo se ha actualizado con éxito');
        const updated = data.map((w) =>
          w.workstation_id === id ? { ...w, state: !w.state } : w
        );
        setFilteredWorkstation(updated);
      })
      .catch((error) => {
        console.error('Failed to update workstation state:', error);
        toast.error('Error al actualizar el estado de la estación de trabajo');
      });
  };

  const handleSelectWorkstation = (workstation: Workstation) => {
    setSelectedWorkstations((prev) =>
      prev.some((ws) => ws.workstation_id === workstation.workstation_id)
        ? prev.filter((ws) => ws.workstation_id !== workstation.workstation_id)
        : [...prev, workstation]
    );
  };

  const updateTerminalName = (workstationId: number, terminalId: number | null) => {
    const terminal = terminals.find((t) => t.id === terminalId);
    const name = terminal ? terminal.name : 'No asignado';
    setFilteredWorkstation((prev) =>
      prev.map((ws) =>
        Number(ws.workstation_id) === workstationId
          ? { ...ws, terminal_name: name }
          : ws
      )
    );
  };

  const handleAddWorkstation = (newWorkstation: Workstation) => {
    setFilteredWorkstation((prev) => [newWorkstation, ...prev]);
    setWorkstations((prev) => [newWorkstation, ...prev]);
  };

  const handleDeleteWorkstation = (deletedId: string) => {
    setFilteredWorkstation((prev) => prev.filter((w) => w.workstation_id !== deletedId));
    setWorkstations((prev) => prev.filter((w) => w.workstation_id !== deletedId));
  };

  const renderTable = () => (
    <div className="mp-table-card workstations-table-card">
      <table className="mp-table">
        <thead className="mp-table-head">
          <tr>
            {showSelection && <th style={{ width: 40 }} />}
            <th>Nombre</th>
            <th className="no-responsive">Descripción</th>
            <th>Terminal</th>
            <th>Activo</th>
            <th>Caja</th>
            <th style={{ width: 100, textAlign: 'center' }}>{isMobile ? <FaCog /> : 'Acciones'}</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {data.map((workstation) => (
            <tr key={workstation.workstation_id}>
              {showSelection && (
                <td>
                  <div className="mp-radio-button">
                    <input
                      id={`checkbox-${workstation.workstation_id}`}
                      type="checkbox"
                      checked={selectedWorkstations.some(
                        (ws) => ws.workstation_id === workstation.workstation_id
                      )}
                      onChange={() => handleSelectWorkstation(workstation)}
                    />
                    <label htmlFor={`checkbox-${workstation.workstation_id}`}></label>
                  </div>
                </td>
              )}
              <td className="font-medium">{workstation.workstation_name}</td>
              <td className="no-responsive" style={{ opacity: 0.7 }}>{workstation.workstation_description}</td>
              <td>{workstation.terminal_name || 'No asignado'}</td>
              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={workstation.state}
                    onChange={() => toggleItemState(workstation.workstation_id)}
                  />
                  <span className="slider round" />
                </label>
              </td>
              <td>
                {workstation.isCaja ? <span className="table-yes">Sí</span> : <span className="table-no">No</span>}
              </td>
              <td>
                <div className="flex items-center justify-center gap-1.5">
                  <button className="mp-btn-icon" onClick={() => handleShowEditModal(workstation)} title="Editar">
                    <HiOutlinePencilSquare size={16} />
                  </button>
                  <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleShowDeleteModal(workstation)} title="Eliminar">
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderNoData = () => (
    <div className="mp-table-card" style={{ padding: '3rem', textAlign: 'center' }}>
      <FaInbox size={32} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
      <div style={{ opacity: 0.6, marginBottom: '1rem' }}>No has creado ningún dispositivo aún!</div>
      <button className="mp-btn-primary" onClick={() => setShowCreateModal(true)}>
        Crear dispositivo
      </button>
    </div>
  );

  return (
    <div>
      {data.length > 0 ? renderTable() : renderNoData()}

      <CreateWorkstation
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        currentData={data}
        setCurrentData={setFilteredWorkstation}
        onWorkstationCreated={handleAddWorkstation}
      />

      <EditWorkstation
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        currentData={data}
        currentWorkstation={currentWorkstation} 
        isOnlyOneCaja={isOnlyOneCaja}
        setCurrentData={(updatedData: Workstation[]) => {
          setFilteredWorkstation(updatedData);
          if (currentWorkstation) {
            const updatedWorkstation = updatedData.find(
              (ws) => ws.workstation_id === currentWorkstation.workstation_id
            );
            if (updatedWorkstation) {
              updateTerminalName(
                Number(updatedWorkstation.workstation_id),
                updatedWorkstation.default_terminal_id ? Number(updatedWorkstation.default_terminal_id) : null
              );
            }
          }
        }}
      />

      <DeleteWorkstation
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        currentWorkstation={currentWorkstation}
        currentData={data}
        setCurrentData={setFilteredWorkstation}
        onWorkstationDeleted={handleDeleteWorkstation}
      />
    </div>
  );
};

export default Table;
